/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';

import usePortal from 'react-useportal';

import { BackButton } from '../Clickables/Controls/BackButton/BackButton';
import { CloseButton } from '../Clickables/Controls/CloseButton/CloseButton';
import { ForwardButton } from '../Clickables/Controls/ForwardButton/ForwardButton';

export interface IModalProps {
  onClose?: () => void;
  onOpen?: () => void;
  isOpen?: boolean;
  children?: React.ReactNode | React.ReactNode[];
  dataTestId?: string;
  hasPrevious: boolean;
  onPreviousClicked: () => void;
  hasNext: boolean;
  onNextClicked: () => void;
}

/**
 * Component for displaying a modal view of a list of post on desktop devices.
 * It takes care of the modal layout and navigation.
 *
 * @component
 * @param {() => void} [props.onClose] - Function to call when the modal is closed.
 * @param {() => void} [props.onOpen] - Function to call when the modal is opened.
 * @param {boolean} [props.isOpen] - Flag to force the modal to be open.
 * @param {React.ReactNode | React.ReactNode[]} [props.children] - The content to display inside the modal (list of posts).
 * @param {string} [props.dataTestId] - The data-testid attribute for testing purposes.
 * @param {boolean} props.hasPrevious - Flag indicating if there is a previous post.
 * @param {() => void} props.onPreviousClicked - Function to call when the previous button is clicked.
 * @param {boolean} props.hasNext - Flag indicating if there is a next post.
 * @param {() => void} props.onNextClicked - Function to call when the next button is clicked.
 *
 * @returns {JSX.Element} The rendered modal component.
 */
export const PostViewModalDesktop = ({
  onClose,
  onOpen,
  isOpen: forceIsOpen,
  children,
  dataTestId,
  hasPrevious,
  onPreviousClicked,
  hasNext,
  onNextClicked,
}: IModalProps) => {
  const { closePortal, isOpen, Portal } = usePortal({
    isOpen: forceIsOpen,
    closeOnOutsideClick: false,
    onClose,
    onOpen,
  });

  return (
    isOpen && (
      <Portal>
        <div
          className="z-modal fixed top-0 left-0 h-screen w-screen bg-[#000000a6] transition-all"
          onClick={event => {
            event.stopPropagation();
          }}
        >
          <div className="grid h-screen grid-cols-[1fr_minmax(850px,6fr)_1fr] grid-rows-[1fr_minmax(480px,6fr)_1fr]">
            <div className="col-start-3 row-start-1 grid items-start justify-end">
              <CloseButton
                className="m-6"
                data-testid="closebtn"
                id="close-modal-button"
                onClick={closePortal}
                variant="controlLakeFogBg"
              />
            </div>
            <div className="col-start-1 col-end-2 row-start-2 row-end-3 grid items-center justify-start">
              <BackButton
                aria-label="Back to the previous post"
                className={`${hasPrevious ? '' : 'invisible'} m-6`}
                onClick={onPreviousClicked}
                variant="controlLakeFogBg"
              />
            </div>
            <div data-testid={dataTestId} className="row-end- col-start-2 col-end-3 row-start-2">
              {children}
            </div>
            <div className="col-start-3 col-end-4 row-start-2 row-end-3 grid items-center justify-end">
              <ForwardButton
                aria-label="Forward to the next post"
                className={`${hasNext ? '' : 'invisible'} m-6`}
                onClick={onNextClicked}
                variant="controlLakeFogBg"
              />
            </div>
          </div>
        </div>
      </Portal>
    )
  );
};
